import React from 'react';
import { Autocomplete, TextField, Chip, Box, Stack, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getUserStatusMeta } from './components/UserStatusLegend';
import { useTranslation } from 'react-i18next';

interface OptionType {
  id: string;
  label: string;
  status?: number | null;
}

interface AutocompleteProps {
  options: OptionType[];
  selectedOptions: OptionType[];
  onChange: (selectedOptions: OptionType[]) => void;
  onRemove: (optionId: string) => void;
  id: string;
  label: string;
  placeholder: string;
  showStatusIndicators?: boolean;
  disabled?: boolean;
}

export default function AutocompleteComponent({
  options,
  selectedOptions,
  onChange,
  onRemove,
  id,
  label,
  placeholder,
  showStatusIndicators = false,
  disabled = false,
}: AutocompleteProps) {
  const { t } = useTranslation('settings');
  return (
    <Autocomplete
      multiple
      disabled={disabled}
      loadingText="Carregando..."
      noOptionsText="Sem opção"
      disableClearable
      id={id}
      options={options}
      value={selectedOptions}
      getOptionLabel={(option) => option?.label ?? ''}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onChange={(_event, newValue, reason) => {
        const beforeIds = selectedOptions.map((option) => option.id);
        const afterIds = newValue.map((option) => option.id);

        const removedIds = beforeIds.filter((optionId) => !afterIds.includes(optionId));

        if (reason === 'removeOption' || reason === 'clear') {
          removedIds.forEach((removedId) => onRemove(removedId));
          return;
        }

        onChange(newValue);
      }}
      renderTags={(value: OptionType[], getTagProps) =>
        value.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index }) as ReturnType<typeof getTagProps> & { key?: React.Key };
          void key;
          const statusMeta = getUserStatusMeta(option.status, t);

          return (
            <Chip
              key={option.id}
              {...tagProps}
              icon={
                showStatusIndicators ? (
                  <Tooltip title={statusMeta.label} arrow enterTouchDelay={0} leaveTouchDelay={200}>
                    <Box sx={{ color: statusMeta.color, display: 'flex', alignItems: 'center' }}>
                      {statusMeta.icon}
                    </Box>
                  </Tooltip>
                ) : undefined
              }
              label={option.label}
              onDelete={() => onRemove(option.id)}
              deleteIcon={<CloseIcon />}
              sx={{
                ...(showStatusIndicators
                  ? {
                      '& .MuiChip-icon': {
                        marginLeft: '10px',
                        marginRight: '-4px',
                        color: `${statusMeta.color} !important`,
                      },
                      '& .MuiChip-icon svg': {
                        color: `${statusMeta.color} !important`,
                      },
                    }
                  : {}),
                '& .MuiChip-deleteIcon': {
                  color: 'inherit',
                },
                '& .MuiChip-deleteIcon:hover': {
                  color: 'inherit',
                  background: 'rgba(255, 123, 123, 0.3)',
                  borderRadius: '16px',
                  transition: '200ms linear',
                },
                '& .MuiChip-deleteIcon svg': {
                  color: 'inherit',
                },
              }}
            />
          );
        })
      }
      renderOption={(props, option) => {
        const { key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & { key?: React.Key };
        void key;
        const statusMeta = getUserStatusMeta(option.status, t);

        return (
          <Box
            key={option.id}
            component="li"
            {...optionProps}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {showStatusIndicators && (
              <Box sx={{ color: statusMeta.color, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                {statusMeta.icon}
              </Box>
            )}

            <Stack spacing={0} sx={{ minWidth: 0 }}>
              <Box component="span" sx={{ color: '#3C4043', fontWeight: 500, lineHeight: 1.2 }}>
                {option.label}
              </Box>
            </Stack>
          </Box>
        );
      }}
      sx={{
        width: '100%',
        borderRadius: '0.625rem',
        backgroundColor: '#F1F1F1',
        '& .MuiOutlinedInput-root': {
          borderRadius: '0.625rem',
          '& input': {
            backgroundColor: 'transparent',
            fontSize: '16px',
          },
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#c9c9c9',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: '#c9c9c9',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: '#c9c9c9',
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={label}
          placeholder={placeholder}
        />
      )}
    />
  );
}
